import React, { useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import {
  TableColumn,
  TagColumn,
  SortState,
  TableRow,
  UntypedRow,
  ColumnKey,
  FilterValue,
  TableFilters,
  TableFilterOptions,
  ServerFilterOption,
} from '../../types/responsiveTable';
import { MAX_VALUE_LENGTH } from '../../utils/tableView';
import { NumericFilter, NumericFilterValue } from '../NumericFilter/NumericFilter';
import { Calendar } from 'primereact/calendar';
import { PrimeIcons } from 'primereact/api';
import { getTagFilterOptions, TagFilterOption } from '../ResponsiveTable/tableUtils';
import { Slider } from 'primereact/slider';
import { classNames } from 'primereact/utils';
import { FormField } from '../FormField/FormField';
import { Capacitor } from '@capacitor/core';
import { useLocaleContext } from '../../context/LocaleContext';
import { capitalize } from '../../utils/textUtils';

/**
 * The option lists of the tag and dropdown filters of a column: the options the server sent for it, else the values of the rows.
 * The filter inputs and the applied filter chips read the same lists, so a value has the same label in both.
 */
export const getTagOptions = <T extends TableRow>(
  column: TagColumn<T>,
  filterOptions: TableFilterOptions | undefined,
  data: T[],
): TagFilterOption[] => {
  // Server option values are not typed by the row, so they are asserted to a cell value (type-only casts below).
  type CellValue = T[ColumnKey<T>];
  if (filterOptions && column.accessor in filterOptions) {
    const serverOptions = filterOptions[column.accessor];
    return (
      serverOptions.map((opt: ServerFilterOption) => {
        if (typeof opt.value === 'boolean') {
          const label = column.textKeyFromCallBack
            ? column.textKeyFromCallBack(opt.value as CellValue, {} as T)
            : String(opt.value);
          const color = column.colorFromCallBack(opt.value as CellValue, {} as T);
          return {
            value: opt.value,
            label,
            color,
          };
        }
        if (opt.name) {
          const color = column.colorFromCallBack(opt.id as CellValue, {} as T);
          return {
            value: opt.id,
            label: opt.name,
            color,
          };
        }
        if (opt.value !== undefined) {
          const mockRow = { [column.accessor]: opt.value } as T;
          const label = column.textKeyFromCallBack
            ? column.textKeyFromCallBack(opt.value as CellValue, mockRow)
            : opt.label || String(opt.value);
          const color = column.colorFromCallBack(opt.value as CellValue, mockRow);
          return {
            value: opt.value,
            label,
            color,
          };
        }
        if (typeof opt === 'string') {
          const mockRow = { [column.accessor]: [opt] } as T;
          const label = column.textKeyFromCallBack ? column.textKeyFromCallBack(opt as CellValue, mockRow) : capitalize(opt);
          const color = column.colorFromCallBack(opt as CellValue, mockRow);
          return {
            value: opt,
            label,
            color,
          };
        }
        return opt;
        // type-only cast: an option whose label callback returns undefined gets its value as text below (#235)
      }) as TagFilterOption[]
    )
      .map((opt) => (opt.label === undefined ? { ...opt, label: String(opt.value) } : opt))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  return getTagFilterOptions(data, column);
};

const getDropDownValues = <T extends TableRow>(column: TableColumn<T>, data: T[]): string[] => {
  const dropdownOptions: string[] = [];
  data.forEach((row) => {
    const value =
      'valueFromCallBack' in column
        ? (column.valueFromCallBack?.(row[column.accessor], row) ?? ['-'])
        : (row[column.accessor] ?? ['-']);
    if (Array.isArray(value)) {
      value.forEach((v) => dropdownOptions.push(v));
    } else {
      dropdownOptions.push(value as string);
    }
  });
  return [...new Set(dropdownOptions)].filter((option) => option !== '-').sort();
};

export const getDropdownOptions = <T extends TableRow>(
  column: TableColumn<T>,
  filterOptions: TableFilterOptions | undefined,
  data: T[],
): Array<{ label: string; value: string }> => {
  if (filterOptions && column.accessor in filterOptions) {
    const serverOptions = filterOptions[column.accessor];
    return (
      serverOptions.map((opt: ServerFilterOption) => {
        if (opt.id && opt.name) {
          return {
            label: opt.name,
            value: opt.id,
          };
        }
        if (typeof opt === 'string') {
          return {
            label: capitalize(opt),
            value: opt,
          };
        }
        return {
          label: opt.label || opt.value || opt,
          value: opt.value || opt.id || opt,
        };
        // type-only cast: an option without label or value keeps the raw option as its label (#177)
      }) as Array<{ label: string; value: string }>
    ).sort((a, b) => a.label.localeCompare(b.label));
  }

  return getDropDownValues(column, data).map((opt) => ({
    label: opt,
    value: opt,
  }));
};

const renderTagFilterItem = (option: TagFilterOption | undefined) => {
  if (!option) return <div className='p-multiselect-label-empty'>empty</div>;

  return option.value === '-' ? (
    '-'
  ) : (
    <Tag value={option.label} {...(option.color === 'primary' ? {} : { severity: option.color })} />
  );
};

interface TableFilterProps<T extends TableRow = UntypedRow> {
  columns: TableColumn<T>[];
  filters: TableFilters;
  data: T[];
  filterOptions?: TableFilterOptions;
  loading?: boolean;
  onFilterChange: (key: string, values: FilterValue[], isSliderFilters?: boolean) => void;
  isSlider?: boolean;
  showFilters: boolean;
  sortState: SortState;
  onSort: (field: string) => void;
}

export const TableFilter = <T extends TableRow = UntypedRow>({
  columns: headers,
  filters,
  data,
  filterOptions,
  loading,
  onFilterChange,
  isSlider = false,
  showFilters,
  sortState,
  onSort,
}: TableFilterProps<T>) => {
  const { locale } = useLocaleContext();
  const multiSelectRefs = useRef<Record<string, MultiSelect | null>>({});

  const handleShow = (columnKey: string) => {
    const multiSelectRef = multiSelectRefs.current[columnKey];
    if (multiSelectRef) {
      const multiSelectElement = multiSelectRef.getElement() as HTMLElement;

      if (multiSelectElement) {
        const tableContainer = multiSelectElement.closest('table')?.parentElement as HTMLElement;

        if (tableContainer && tableContainer.style.overflowY === 'auto') {
          const multiSelectRect = multiSelectElement.getBoundingClientRect();
          const containerRect = tableContainer.getBoundingClientRect();

          const isLeftVisible = multiSelectRect.left >= containerRect.left;
          const isRightVisible = multiSelectRect.right <= containerRect.right;

          if (!isLeftVisible || !isRightVisible) {
            const offset = 100;
            let scrollDirection = 0;

            if (!isLeftVisible) {
              const hiddenWidth = containerRect.left - multiSelectRect.left;
              scrollDirection = -(hiddenWidth + offset);
            } else if (!isRightVisible) {
              const hiddenWidth = multiSelectRect.right - containerRect.right;
              scrollDirection = hiddenWidth + offset;
            }

            const currentScrollLeft = tableContainer.scrollLeft;
            const newScrollLeft = Math.max(0, currentScrollLeft + scrollDirection);

            tableContainer.scrollTo({
              left: newScrollLeft,
              behavior: 'smooth',
            });

            setTimeout(() => {
              if (multiSelectRef) {
                multiSelectRef.show();
              }
            }, 400);

            return true;
          }
        }
      }
    }
    return false;
  };

  const handleOnFilterChange = (column: TableColumn<T>, value: FilterValue | FilterValue[]) => {
    const key = column.accessor;
    let values: FilterValue[];

    switch (column.type) {
      case 'calendar':
      case 'dropdown':
      case 'tag':
      case 'boolean':
      case 'progress':
        values = value as FilterValue[];
        break;
      default:
        // the number filter reports a cleared input as [] (truthy), which must stay an empty list (#128)
        values = Array.isArray(value) && !value.length ? [] : value ? [value as FilterValue] : [];
    }

    onFilterChange(key, values, isSlider);
  };

  const filtersConfig = (column: TableColumn<T>, index: number) => {
    const filterValue = filters[column.accessor];
    return (
      <>
        {column.type === 'string' && (
          <FormField fullWidth>
            <InputText
              id={`filter-${column.label}`}
              // type-only cast: the text filter is stored as a one-item array, but the input reads it as a string
              value={(filterValue || '') as unknown as string}
              maxLength={MAX_VALUE_LENGTH}
              onChange={(e) => handleOnFilterChange(column, e.target.value)}
            />
          </FormField>
        )}
        {column.type === 'boolean' && (
          <FormField fullWidth>
            <MultiSelect
              inputId={`filter-${column.label}`}
              ref={(el) => (multiSelectRefs.current[`${column.accessor}-${index}`] = el)}
              value={filterValue || []}
              options={
                column.allowNullFilter
                  ? [
                      { label: column.trueText || 'Sí', value: true },
                      { label: column.falseText || 'No', value: false },
                      { label: 'No aplica', value: null },
                    ]
                  : [
                      { label: column.trueText || 'Sí', value: true },
                      { label: column.falseText || 'No', value: false },
                    ]
              }
              onChange={(e) => handleOnFilterChange(column, e.value)}
              appendTo={'self'}
              panelStyle={{ minWidth: '200px', maxHeight: '300px', zIndex: 9999 }}
              autoOptionFocus={false}
              focusOnHover={false}
              selectOnFocus={false}
              onShow={() => {
                handleShow(`${column.accessor}-${index}`);
              }}
              itemTemplate={(option) => (
                <span className={`boolean-value ${option.value === true ? 'true' : option.value === false ? 'false' : 'na'}`}>
                  <i
                    className={`pi ${
                      option.value === true
                        ? column.trueIcon || PrimeIcons.CHECK_CIRCLE
                        : option.value === false
                          ? column.falseIcon || PrimeIcons.TIMES_CIRCLE
                          : PrimeIcons.MINUS_CIRCLE
                    }`}
                  ></i>
                  <span className='boolean-text'>{option.label}</span>
                </span>
              )}
            />
          </FormField>
        )}
        {column.type === 'tag' && (
          <FormField fullWidth>
            <MultiSelect
              inputId={`filter-${column.label}`}
              ref={(el) => (multiSelectRefs.current[`${column.accessor}-${index}`] = el)}
              className='tag-multiselect'
              value={filterValue || []}
              options={getTagOptions(column, filterOptions, data)}
              optionLabel='label'
              optionValue='value'
              dataKey='value'
              itemTemplate={renderTagFilterItem}
              selectedItemTemplate={(value: FilterValue) => {
                const allOptions = getTagOptions(column, filterOptions, data);
                const option = allOptions.find((o) => o.value === value);
                return option ? renderTagFilterItem(option) : <div className='p-multiselect-label-empty'>empty</div>;
              }}
              onChange={(e) => handleOnFilterChange(column, e.value)}
              appendTo={'self'}
              panelStyle={{ minWidth: '200px', maxHeight: '300px', zIndex: 9999 }}
              autoOptionFocus={false}
              focusOnHover={false}
              selectOnFocus={false}
              onShow={() => {
                handleShow(`${column.accessor}-${index}`);
              }}
            />
          </FormField>
        )}
        {column.type === 'dropdown' && (
          <FormField fullWidth>
            <MultiSelect
              inputId={`filter-${column.label}`}
              ref={(el) => (multiSelectRefs.current[`${column.accessor}-${index}`] = el)}
              value={filterValue || []}
              options={getDropdownOptions(column, filterOptions, data)}
              optionLabel='label'
              optionValue='value'
              itemTemplate={(option) => (option?.label?.[0] === '-' ? '-' : (option?.label?.toString() ?? option?.toString()))}
              onChange={(e) => handleOnFilterChange(column, e.value)}
              appendTo={'self'}
              panelStyle={{ minWidth: '200px', maxHeight: '300px', zIndex: 9999 }}
              autoOptionFocus={false}
              focusOnHover={false}
              selectOnFocus={false}
              onShow={() => {
                handleShow(`${column.accessor}-${index}`);
              }}
              showSelectAll
              filter
              filterPlaceholder='Buscar...'
            />
          </FormField>
        )}
        {column.type === 'number' && (
          <FormField fullWidth>
            <NumericFilter
              inputId={`filter-${column.label}`}
              value={(filterValue || []) as NumericFilterValue[]}
              onChange={(value) => {
                if (value === null) {
                  handleOnFilterChange(column, []);
                } else if (value.value !== null && !isNaN(value.value)) {
                  handleOnFilterChange(column, value);
                }
              }}
              isCurrency={'isCurrency' in column && column.isCurrency}
              currencyLocale={'currencyLocale' in column ? column.currencyLocale : 'es-ES'}
              currencyCode={'currencyCode' in column ? column.currencyCode : 'EUR'}
              placeholder={`Filtrar por ${column.label.toLowerCase()}`}
            />
          </FormField>
        )}
        {column.type === 'calendar' && (
          <FormField fullWidth>
            <Calendar
              inputId={`filter-${column.label}`}
              locale={locale}
              // type-only cast: '' (no filter yet) is passed as it was before; the Calendar types do not accept it
              value={(filterValue || '') as Date[]}
              onChange={(e) => {
                if (e.value && Array.isArray(e.value)) {
                  const validDates = e.value.filter((d) => d !== null);

                  if (validDates.length > 0) {
                    handleOnFilterChange(column, validDates);
                  }
                } else if (
                  !e.value ||
                  (Array.isArray(e.value) && (e.value as (Date | null)[]).every((d: Date | null) => d === null))
                ) {
                  handleOnFilterChange(column, []);
                }
              }}
              selectionMode='range'
              readOnlyInput
              showIcon
              showButtonBar
              todayButtonClassName='p-button-secondary'
              clearButtonClassName='p-button-secondary'
              maxDate={new Date()}
              dateFormat='dd/mm/yy'
              showTime={false}
              touchUI={Capacitor.isNativePlatform() || window.matchMedia('(hover: none)').matches}
            />
          </FormField>
        )}
        {column.type === 'progress' && (
          <FormField fullWidth>
            <div className='progress-filter'>
              <Slider
                id={`filter-${column.label}`}
                value={filterValue as [number, number] | undefined}
                onChange={(e) => {
                  const [min, max] = e.value as number[];
                  if (min <= max) {
                    handleOnFilterChange(column, e.value);
                  }
                }}
                range
                min={0}
                max={100}
                step={1}
              />
              <div className={classNames('progress-filter-values', { inactive: !filterValue?.[0] && !filterValue?.[1] })}>
                <small>{(filterValue?.[0] as number | undefined) || '0'}%</small>
                <small>{(filterValue?.[1] as number | undefined) || '100'}%</small>
              </div>
            </div>
          </FormField>
        )}
      </>
    );
  };

  const getFilters = (header: TableColumn<T>, index: number) => {
    const labelContent = (
      // Only the column name sits in the <label>: a control inside it would join the filter's accessible name (#391)
      <div className='header-label-container'>
        <span>
          <label htmlFor={`filter-${header.label}`}>{header.label}</label>
        </span>
        {!isSlider && header.type !== 'avatar' && header.sortable !== false && (
          <i
            className={classNames('sort-icon pi', {
              [PrimeIcons.SORT_ALT]: !sortState.field || sortState.field !== header.accessor,
              [PrimeIcons.SORT_AMOUNT_UP_ALT]: sortState.field === header.accessor && sortState.direction === 'asc',
              [PrimeIcons.SORT_AMOUNT_DOWN]: sortState.field === header.accessor && sortState.direction === 'desc',
            })}
            role='button'
            tabIndex={0}
            aria-label={`Ordenar por ${header.label}`}
            onClick={(e) => {
              // Keeps the click on the icon from focusing or opening the filter below it (#361). Since #391 the icon is
              // outside the <label>, so the preventDefault is a guard in case it moves back inside.
              e.preventDefault();
              e.stopPropagation();
              onSort(header.accessor);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              onSort(header.accessor);
            }}
          />
        )}
      </div>
    );

    return (
      <div className='table-header' key={header.label}>
        <FormField fullWidth>
          {labelContent}
          {showFilters && header.filterable !== false && filtersConfig(header, index)}
        </FormField>
      </div>
    );
  };

  return loading ? (
    <>
      {Array.from({ length: headers.length }).map((_, colIndex) => {
        const isAvatar = headers[colIndex].type === 'avatar';
        if (isAvatar) {
          return <th key={colIndex}></th>;
        }
        return (
          <th key={colIndex}>
            <Skeleton width='80%' height='22px' />
          </th>
        );
      })}
      <th></th>
    </>
  ) : (
    headers.map((header, index) =>
      isSlider ? (
        // The mobile filter panel only lists filters: a column without one has nothing to show there
        header.filterable !== false && getFilters(header, index)
      ) : (
        <th className={header.type} key={header.label}>
          {getFilters(header, index)}
        </th>
      ),
    )
  );
};
